import React, { useCallback, useContext, useMemo } from 'react';

import AccountContext from 'contexts/AccountContext';
import BaseViewsContext from 'contexts/BaseViewsContext';
import ComparisonContext from 'contexts/ComparisonContext';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import ReportGadgetsContext from 'contexts/ReportGadgetsContext';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';
import InsightsViewScopeContext from 'contextProviders/InsightsContentProvider/InsightsViewScopeContext';
import { DefaultDatasetFiltersContext } from 'contextProviders/SplashScreenProviders/DatasetProviders/DefaultDatasetFiltersProvider';
import useDateScope from 'hooks/useDateScope';
import useFieldDefinitions from 'hooks/useFieldDefinitions';
import useGetMetricIdsFromVis from 'hooks/useGetMetricIdsFromVis';
import useRegisterApplyReportFilters from 'hooks/useRegisterApplyReportFilters';
import useRegisterInsightsView from 'hooks/useRegisterInsightsView';
import { AnswerSourceFilter } from 'services/streamInsightsQuery';

import buildReportInsightsView from './buildReportInsightsView';
import mergeAgentFiltersIntoDrillDowns from './mergeAgentFiltersIntoDrillDowns';

// Publishes the report's live on-screen state to the AI Insights Chat, and scopes
// the gadgets it wraps so their rendered figures register against this report.
//
// Wraps the report rather than sitting beside it so that the structural view and
// the per-gadget values can't disagree about which entity they belong to — the
// case that matters is a popup report over a dashboard, where the registered
// Insights entity is still the dashboard.
//
// Mount it INSIDE WeekStartsOnOverrideProviderExceptForPopups: useDateScope reads
// both FilterPlatesContext (for date drill-downs) and WeekStartsOnOverrideContext
// (for week boundaries), so mounting it any higher silently resolves a range like
// "Last week" against the account's week start rather than the report's override.
const RegisterReportInsightsView = ({
  reportId,
  cards,
  isDefaultFiltersDisabled,
  isDisabled,
  children,
}: {
  reportId: string;
  cards: CanvasCard.Card[];
  isDefaultFiltersDisabled: boolean;
  // Popups, wallboards and headless renders aren't the user's in-focus content,
  // and the entity itself isn't registered for them either.
  isDisabled: boolean;
  children: JSX.Element;
}) => {
  const {
    scopeAsFixed,
    drillDownsAsFixed,
    setDrillDowns,
    dataTypes,
    fieldRedList,
  } = useContext(FilterPlatesContext);
  const { currentComparison } = useContext(ComparisonContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const { reportGadgetsLookup } = useContext(ReportGadgetsContext);
  const { defaultDatasetFiltersLookup } = useContext(
    DefaultDatasetFiltersContext,
  );
  const { weekStartsOn: accountWeekStartsOn } = useContext(AccountContext);
  const { weekStartsOnOverride } = useContext(WeekStartsOnOverrideContext);
  const dateScope = useDateScope({});
  const getMetricIds = useGetMetricIdsFromVis();

  const view = useMemo(() => {
    if (isDisabled) {
      return null;
    }
    return buildReportInsightsView({
      cards,
      reportGadgetsLookup,
      dateScope,
      scopeAsFixed,
      drillDownsAsFixed,
      baseViews,
      metricOptionsLookup,
      defaultDatasetFiltersLookup,
      comparison: currentComparison,
      weekStartsOn: weekStartsOnOverride ?? accountWeekStartsOn,
      isReportDefaultFiltersDisabled: isDefaultFiltersDisabled,
      getMetricIds,
    });
  }, [
    isDisabled,
    cards,
    reportGadgetsLookup,
    dateScope,
    scopeAsFixed,
    drillDownsAsFixed,
    baseViews,
    metricOptionsLookup,
    defaultDatasetFiltersLookup,
    currentComparison,
    weekStartsOnOverride,
    accountWeekStartsOn,
    isDefaultFiltersDisabled,
    getMetricIds,
  ]);

  useRegisterInsightsView({ entityId: reportId, view });

  // The report's filterable fields, keyed by name — both the validation set (an
  // agent filter on a field the report can't filter is skipped) and the source of
  // each field's exact type, which the wire dropped.
  const { fieldDefinitions } = useFieldDefinitions(dataTypes);
  const fieldByName = useMemo(() => {
    const map = new Map<string, FleetOps.Field>();
    fieldDefinitions.forEach((f) => map.set(f.field, f));
    return map;
  }, [fieldDefinitions]);

  // Carry an agent-applied filter through to the report's drill-downs: transient,
  // accumulates (AND) onto whatever's already applied, skips fields the report
  // can't filter and exact duplicates.
  const applyAgentFilters = useCallback(
    (filters: AnswerSourceFilter[]) => {
      setDrillDowns((prev) =>
        mergeAgentFiltersIntoDrillDowns({
          prev,
          filters,
          fieldByName,
          fieldRedList,
        }),
      );
    },
    [setDrillDowns, fieldByName, fieldRedList],
  );

  useRegisterApplyReportFilters({
    entityId: isDisabled ? null : reportId,
    apply: applyAgentFilters,
  });

  const scope = useMemo(
    () => ({ entityId: isDisabled ? null : reportId }),
    [isDisabled, reportId],
  );

  return (
    <InsightsViewScopeContext.Provider value={scope}>
      {children}
    </InsightsViewScopeContext.Provider>
  );
};

export default RegisterReportInsightsView;
